import { Tooltip } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ignoreMinutesIfZero } from 'components/organisms/resource-management/utils/dates';
import { BodyShort01 } from 'components/styles';

const OverAllocatedStyles = css`
  background: var(--red-20);
  border-top: 2px solid var(--red-50);
`;
const AllocatedInBetweenStyles = css`
  background: var(--yellow-20);
  border-top: 2px solid var(--yellow-50);
`;

const UnderAllocatedStyles = css`
  background: var(--green-10);
  border-top: 2px solid var(--green-40);
`;

enum TotalAllocationMode {
  UnderAllocated,
  AllocatedInBetween,
  OverAllocated,
}

const Wrapper = styled.div<{
  allocationMode: TotalAllocationMode;
  $widthPercentage: number;
  $showCursor: boolean;
}>`
  ${BodyShort01};
  cursor: ${({ $showCursor }) => ($showCursor ? 'pointer' : 'default')};
  padding: 0 var(--spacing-05);
  display: flex;
  gap: var(--spacing-02);
  width: 100%;
  height: 100%;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${({ $widthPercentage }) => `${$widthPercentage}%`};
    max-width: 100%;
    z-index: -1;
    ${({ allocationMode }) =>
      allocationMode === TotalAllocationMode.OverAllocated
        ? OverAllocatedStyles
        : allocationMode === TotalAllocationMode.AllocatedInBetween
        ? AllocatedInBetweenStyles
        : UnderAllocatedStyles};
  }
`;
const SecondaryValue = styled.span`
  color: var(--gray-60);
  font-size: var(--font-size-sm);
`;
const AllocationWrapper = styled.span`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const HoursSection = styled.td`
  padding-left: var(--spacing-05);
  text-align: right;
`;

const TooltipContent: FC<{
  budgetedHoursRange?: { lowerLimit: number; upperLimit: number };
  totalAllocatedInMinutes: number;
  remainingHoursInMinutes?: number;
}> = ({
  budgetedHoursRange,
  totalAllocatedInMinutes,
  remainingHoursInMinutes,
}) => {
  return (
    <table>
      <tr>
        <td>
          <FormattedMessage id="budgeted_hours" />:
        </td>
        <HoursSection>
          {budgetedHoursRange != null ? (
            budgetedHoursRange.upperLimit !== budgetedHoursRange.lowerLimit ? (
              `${ignoreMinutesIfZero(
                budgetedHoursRange.lowerLimit,
              )} - ${ignoreMinutesIfZero(budgetedHoursRange.upperLimit)}`
            ) : (
              ignoreMinutesIfZero(budgetedHoursRange.upperLimit)
            )
          ) : (
            <span>-</span>
          )}
        </HoursSection>
      </tr>
      <tr>
        <td>
          <FormattedMessage id="resource_management.allocation.total_allocated.label" />
          :
        </td>
        <HoursSection>
          {ignoreMinutesIfZero(totalAllocatedInMinutes)}
        </HoursSection>
      </tr>
      <br />
      <tr>
        <td>
          <FormattedMessage id="resource_management.allocation.remaining_hours.label" />
          :
        </td>
        <HoursSection>
          {remainingHoursInMinutes != null ? (
            ignoreMinutesIfZero(remainingHoursInMinutes)
          ) : (
            <span>-</span>
          )}
        </HoursSection>
      </tr>
    </table>
  );
};

interface Props {
  budgetedHoursRange?: { lowerLimit: number; upperLimit: number };
  totalAllocation: number;
  isTimesheetFeatureEnabled: boolean;
  showDetailedView: boolean;
}
export const TemplateAllocation: FC<Props> = ({
  budgetedHoursRange,
  totalAllocation,
  isTimesheetFeatureEnabled,
  showDetailedView,
}) => {
  const hasBudgetedHoursSet =
    isTimesheetFeatureEnabled &&
    budgetedHoursRange != null &&
    budgetedHoursRange.upperLimit !== 0;
  const allocationMode =
    budgetedHoursRange != null &&
    totalAllocation > budgetedHoursRange.upperLimit
      ? TotalAllocationMode.OverAllocated
      : budgetedHoursRange != null &&
        totalAllocation <= budgetedHoursRange.upperLimit &&
        totalAllocation > budgetedHoursRange.lowerLimit
      ? TotalAllocationMode.AllocatedInBetween
      : TotalAllocationMode.UnderAllocated;

  const allocatedHoursPercentage =
    hasBudgetedHoursSet && budgetedHoursRange != null
      ? (totalAllocation / budgetedHoursRange.upperLimit) * 100
      : 0;

  return (
    <Wrapper
      allocationMode={allocationMode}
      $widthPercentage={
        isTimesheetFeatureEnabled &&
        budgetedHoursRange != null &&
        budgetedHoursRange.upperLimit !== 0
          ? Math.max(4, allocatedHoursPercentage)
          : 0
      }
      $showCursor={isTimesheetFeatureEnabled && showDetailedView}
    >
      <Tooltip
        title={
          isTimesheetFeatureEnabled && showDetailedView ? (
            <TooltipContent
              budgetedHoursRange={
                hasBudgetedHoursSet && budgetedHoursRange != null
                  ? budgetedHoursRange
                  : undefined
              }
              totalAllocatedInMinutes={totalAllocation}
              remainingHoursInMinutes={
                hasBudgetedHoursSet && budgetedHoursRange != null
                  ? budgetedHoursRange.upperLimit - totalAllocation
                  : undefined
              }
            />
          ) : null
        }
        placement="right"
      >
        <AllocationWrapper>
          {ignoreMinutesIfZero(totalAllocation)}
          {isTimesheetFeatureEnabled && budgetedHoursRange != null ? (
            <SecondaryValue>
              {allocatedHoursPercentage.toFixed(0)}%
            </SecondaryValue>
          ) : null}
        </AllocationWrapper>
      </Tooltip>
    </Wrapper>
  );
};
